import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../layouts';
import { PhoneInput } from '../components/form';
import { formatPhoneNumberForAPI } from '../utils/formatters';
import analyticsService from '../services/analytics';
import googleAnalytics from '../services/googleAnalytics';

const LoginPage: React.FC = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dialCode, setDialCode] = useState('+61');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({
    phoneNumber: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track if we've already logged a page view for this session
  const hasTrackedPageView = useRef(false);
  
  // Get the redirect path from location state or default to home
  const from = location.state?.from?.pathname || '/';
  
  // Get success message from location state if available
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Clear the location state to prevent showing the message again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Track page view when component mounts
  useEffect(() => {
    if (!hasTrackedPageView.current) {
      try {
        analyticsService.trackLoginPageView({
          referrer: document.referrer || 'direct',
          fromPath: from,
          hasSuccessMessage: !!successMessage,
          userAgent: navigator.userAgent
        });
        
        hasTrackedPageView.current = true;
        console.log('📊 Analytics: LoginPage view tracked');
      } catch (error) {
        console.error('Analytics error:', error);
        // Don't let analytics errors break the app
      }
    }
  }, [from, successMessage]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!phoneNumber || !password) {
      setError('Please enter both phone number and password');
      return;
    }
    
    // Format phone number for API
    const formattedPhoneNumber = formatPhoneNumberForAPI(phoneNumber, dialCode);
    
    // Track login attempt
    try {
      analyticsService.trackLoginAttempt({
        phoneNumber: formattedPhoneNumber,
        hasPhoneNumber: !!phoneNumber,
        hasPassword: !!password,
        fromPath: from
      });
    } catch (error) {
      console.error('Analytics error:', error);
    }
    
    try {
      setError('');
      setIsSubmitting(true);
      
      // Call login function from AuthContext
      await login(formattedPhoneNumber, password);
      
      // Track successful login
      try {
        googleAnalytics.trackLogin();
        analyticsService.trackLogin(true, {
          phoneNumber: formattedPhoneNumber,
          fromPath: from,
          redirectTo: from
        });
        console.log('📊 Analytics: Login success tracked');
      } catch (error) {
        console.error('Analytics error:', error);
      }
      
      // Redirect to the page the user was trying to access or home
      navigate(from, { replace: true });
    } catch (err: any) {
      // Track failed login
      try {
        analyticsService.trackLogin(false, {
          phoneNumber: formattedPhoneNumber,
          errorMessage: err.message,
          errorType: err.message === 'UNCONFIRMED_USER' ? 'unconfirmed' : 'authentication_failed',
          fromPath: from
        });
        console.log('📊 Analytics: Login failure tracked');
      } catch (error) {
        console.error('Analytics error:', error);
      }
      
      // Check if this is an unconfirmed user error
      if (err.message === 'UNCONFIRMED_USER') {
        // Store the phone number in localStorage if not already there
        if (!localStorage.getItem('tempPhoneNumber')) {
          localStorage.setItem('tempPhoneNumber', phoneNumber);
        }
        
        // Show a specific error message with a link to verify
        setError('Your account needs to be verified. Please verify your phone number to continue.');
        
        // Don't set isSubmitting to false yet, as we'll keep the button disabled
        // until the user clicks the verify button
        return;
      }
      
      setError(err.message || 'Failed to login. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <MainLayout>
      <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-8">
        <h1 className="text-2xl font-bold text-center mb-6">Login</h1>
        
        {successMessage && (
          <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-6 text-center">
            <svg 
              className="w-8 h-8 mx-auto mb-3 text-green-600" 
              fill="currentColor" 
              viewBox="0 0 20 20" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                fillRule="evenodd" 
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
                clipRule="evenodd"
              />
            </svg>
            <p className="text-green-800 font-medium">{successMessage}</p>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg mb-6 text-center">
            <svg 
              className="w-8 h-8 mx-auto mb-3 text-red-600" 
              fill="currentColor" 
              viewBox="0 0 20 20" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                fillRule="evenodd" 
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" 
                clipRule="evenodd"
              />
            </svg>
            <p className="text-red-800 font-medium">{error}</p>
            
            {/* Show verification button if the error is about unconfirmed user */}
            {error.includes('needs to be verified') && (
              <div className="mt-4">
                <Link 
                  to="/verify-phone" 
                  className="bg-racing-blue text-white py-2 px-4 rounded-lg font-medium hover:bg-racing-blue-dark transition inline-flex items-center"
                >
                  <span>Verify Phone Number</span>
                  <svg 
                    className="w-5 h-5 ml-2" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M9 5l7 7-7 7" 
                    />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="phoneNumber" className="block text-gray-700 font-medium mb-2">
              Phone Number
            </label>
            <PhoneInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              onCountryCodeChange={setDialCode}
              error={errors.phoneNumber}
              setError={(error) => setErrors(prev => ({ ...prev, phoneNumber: error }))}
              required
            />
          </div>
          
          <div className="mb-6">
            <label htmlFor="password" className="block text-gray-700 font-medium mb-2">
              Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg 
                  className="h-5 w-5 text-gray-400" 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 20 20" 
                  fill="currentColor" 
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={() => {
                  if (!password) {
                    setErrors(prev => ({ ...prev, password: 'Password is required' }));
                  } else {
                    setErrors(prev => ({ ...prev, password: '' }));
                  }
                }}
                className={`w-full pl-10 pr-10 py-2 border ${errors.password ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-racing-green focus:border-racing-green`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password}</p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full bg-racing-green text-white py-3 px-4 rounded-lg font-medium hover:bg-racing-green-dark transition flex items-center justify-center ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Logging in...
              </>
            ) : (
              'Login'
            )}
          </button>
        </form>
        
        <div className="mt-4 text-center">
          <Link to="/forgot-password" className="text-deep-navy hover:text-racing-green hover:underline">
            Forgot Password?
          </Link>
        </div>
        
        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-racing-green hover:underline">
              Register
            </Link>
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default LoginPage;
